import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../context";
import { getAssembly } from "./service";
import { CoreContainer, Notification } from "../common/style";
import LoadingSpinner from "./LoadingSpinner";
import { Modal, ModalBody } from "reactstrap";
import { AssemblyFooter, OrderItemLabel } from "./style";
import { useParams } from "react-router-dom";
import { RenderCanvasElementForAssembly } from "../project/style";
import { InternalsDimensionIcon } from "../product/style";
import { size } from "lodash";

const Assembly = () => {
  const [assemblyDrawings, setAssemblyDrawings] = useState(false);
  const { projectInfo: project } = useContext(AppContext);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const canvasRefs = useRef([]);
  const params = useParams();
  const projectId = params.projectId;
  const [renderingDirection, setRenderingDirection] = useState([]);
  const max_dimensions = {
    horizontal: {
      max_height: 570,
      max_width: 42,
    },
    vertical: {
      max_height: 270,
      max_width: 100,
    },
  };

  useEffect(() => {
    const newRenderingDirections = [];

    canvasRefs.current.forEach((canvasRef, index) => {
      const element = canvasRef.current;

      if (!element) return;

      const assembly_drawings_length = assemblyDrawings.length;
      const newIndex =
        index >= assembly_drawings_length
          ? index % assembly_drawings_length
          : index;
      const { height, width } = assemblyDrawings[newIndex];
      const aspectRatio = width / height;

      const renderVertical = width >= height ? "vertical" : "horizontal";
      newRenderingDirections[newIndex] =
        renderVertical === "horizontal" ? "flex-row" : "flex-column";

      const resizeObserver = new ResizeObserver(() => {
        const element_width = element.offsetWidth;
        let newHeight = element_width / aspectRatio;
        const max_height = max_dimensions[renderVertical]["max_height"];
        const max_width = max_dimensions[renderVertical]["max_width"];

        if (newHeight > max_height + 10) {
          window.requestAnimationFrame(() => {
            const resizedWidth =
              ((max_height * aspectRatio) / element_width) * max_width;
            element.style.width = `${resizedWidth}%`;

            newHeight = resizedWidth / aspectRatio;
          });
        }

        if (element.offsetHeight !== newHeight) {
          window.requestAnimationFrame(() => {
            element.style.height = `${newHeight}px`;
          });
        }
      });

      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    });

    setRenderingDirection(newRenderingDirections);
  }, [assemblyDrawings]);

  useEffect(() => {
    fetchAssembly();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        history.back();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const fetchAssembly = () => {
    setIsLoading(true);
    getAssembly({
      projectId: projectId,
    })
      .then((response) => {
        setAssemblyDrawings(response.assembly_drawings);
      })
      .catch(({ error }) => {
        setNotification({ message: error, type: "error" });
      })
      .finally(() => setIsLoading(false));
  };

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const renderLabels = (component, renderingSize, cutlist, showShutters) => {
    const matchedCutlistItem = cutlist.find(
      (item) => item.label === component.label
    );

    if (component.type === "drawer" && component.panels.length <= 3) {
      return null;
    }

    if (
      matchedCutlistItem &&
      matchedCutlistItem.type != "shutter" &&
      showShutters
    ) {
      return null;
    }

    const hasMultiplePanels =
      matchedCutlistItem &&
      matchedCutlistItem.panels &&
      matchedCutlistItem.panels.length > 1;

    let renderStyle = {
      position: "absolute",
      bottom: `${
        component.type === "v_partition"
          ? component.dimensions.height / renderingSize / 2
          : component.type === "space" && component.is_drawer
            ? component.dimensions.height / renderingSize / 5
            : component.type === "shutter"
              ? component.dimensions.height / renderingSize / 2
              : component.dimensions.height / renderingSize
      }mm`,
      left: `${
        component.type === "v_partition"
          ? "0"
          : component.type === "carcass"
            ? component.dimensions.width / renderingSize / 2
            : component.dimensions.width / renderingSize / 3
      }mm`,
      alignContent: "center",
      textAlign: "center",
      opacity: 1,
      writingMode: `${
        component.type === "v_partition" ? "vertical-lr" : "horizontal-tb"
      }`,
      fontSize: renderingSize > 15 ? "2mm" : "3mm",
      ...(hasMultiplePanels && {
        borderRadius: "50%",
        height: renderingSize > 15 ? "15px" : "25px",
        width: renderingSize > 15 ? "15px" : "25px",
        border: "1px solid black",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }),
    };

    return <div style={renderStyle}>{component.label}</div>;
  };

  function ModuleNode({
    module,
    position,
    showShutters,
    renderingSize,
    cutlist,
  }) {
    return (
      <RenderNode
        object={module}
        position={position}
        renderingSize={renderingSize}
      >
        {module.template && (
          <>
            <ComponentNode
              className="base-component"
              component={module.template.base_component}
              renderingSize={renderingSize}
              cutlist={cutlist}
              showShutters={showShutters}
            ></ComponentNode>
            <SpaceNode
              className="root-space"
              space={module.template.root_space}
              renderingSize={renderingSize}
              cutlist={cutlist}
              showShutters={showShutters}
            ></SpaceNode>
            {
              <div className="partitions">
                {module.template.partitions.map((partition, partitionIndex) => (
                  <PartitionNode
                    key={partitionIndex}
                    partition={partition}
                    renderingSize={renderingSize}
                    cutlist={cutlist}
                    showShutters={showShutters}
                  ></PartitionNode>
                ))}
              </div>
            }
            {showShutters && (
              <div className="shutters">
                {module.template.shutters.map((shutter, shutterIndex) => (
                  <ShutterNode
                    className="shutter-node"
                    key={shutterIndex}
                    shutter={shutter}
                    renderingSize={renderingSize}
                    cutlist={cutlist}
                    showShutters={showShutters}
                  ></ShutterNode>
                ))}
              </div>
            )}
          </>
        )}
      </RenderNode>
    );
  }

  function PartitionNode({ partition, renderingSize, cutlist, showShutters }) {
    return (
      <ComponentNode
        component={partition.component}
        renderingSize={renderingSize}
        cutlist={cutlist}
        showShutters={showShutters}
      />
    );
  }

  function ShutterNode({ shutter, renderingSize, cutlist, showShutters }) {
    return (
      <ComponentNode
        component={shutter.component}
        renderingSize={renderingSize}
        cutlist={cutlist}
        showShutters={showShutters}
      />
    );
  }

  function SpaceNode({
    space,
    widthCount = -1,
    heightCount = -1,
    renderingSize,
    cutlist,
    showShutters,
  }) {
    if (!space) return;
    if (space.width) widthCount++;
    if (space.height) heightCount++;

    return (
      <RenderNode object={space} key={space} renderingSize={renderingSize}>
        {space.children &&
          space.children.map((childSpace, spaceIndex) => (
            <>
              <SpaceNode
                key={spaceIndex}
                space={childSpace}
                widthCount={widthCount}
                heightCount={heightCount}
                renderingSize={renderingSize}
                cutlist={cutlist}
                showShutters={showShutters}
              />
            </>
          ))}
        {space.is_drawer && (
          <div className="h-100 pattern border border-black">
            {renderLabels(space, renderingSize, cutlist)}
          </div>
        )}
        {!space.is_root_space && (
          <>
            {space.width && space.width !== 0 && space.width !== "eq" && (
              <div
                className="d-flex position-absolute"
                style={{
                  top: `${widthCount * 2}mm`,
                  left: "0.2mm",
                  width: `${space.dimensions["width"] / renderingSize - 1}mm`,
                  color: "blue",
                }}
              >
                <InternalsDimensionIcon className="w-100" dimension={"width"} />
                <div
                  style={{
                    fontSize: renderingSize > 15 ? "2mm" : "3mm",
                  }}
                  className="w-100 d-flex justify-content-center text-black"
                >
                  <span>{space.width}</span>
                </div>
              </div>
            )}
            {space.height && space.height !== 0 && space.height !== "eq" && (
              <div
                className="d-flex position-absolute"
                style={{
                  top: "0.2mm",
                  left: `${heightCount * 2}mm`,
                  height: `${space.dimensions["height"] / renderingSize - 1}mm`,
                  color: "blue",
                }}
              >
                <InternalsDimensionIcon
                  className="h-100"
                  dimension={"height"}
                />
                <div
                  className="d-flex justify-content-center align-items-center text-black"
                  style={{
                    writingMode: "vertical-rl",
                    fontSize: renderingSize > 15 ? "2mm" : "3mm",
                  }}
                >
                  <span style={{ transform: "rotate(180deg)" }}>
                    {space.height}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </RenderNode>
    );
  }

  function ComponentNode(props) {
    const component = props.component;
    const componentIndex = 0;

    return (
      <RenderNode
        object={component}
        className={`component-node`}
        key={componentIndex}
        renderingSize={props.renderingSize}
        {...props}
      >
        {component.panels.map((panel, panelIndex) => (
          <PanelNode
            key={panelIndex}
            panel={panel}
            componentType={component.type}
            componentIdentifier={component.identifier}
            renderingSize={props.renderingSize}
          ></PanelNode>
        ))}
        {renderLabels(
          component,
          props.renderingSize,
          props.cutlist,
          props.showShutters
        )}
      </RenderNode>
    );
  }

  function PanelNode({
    panel,
    componentType,
    componentIdentifier,
    renderingSize,
  }) {
    let style = {};
    let backgroundColor = componentType === "shutter" ? "white" : "none";

    let border = "1px solid black";
    let backgroundImage = "none";
    let backgroundRepeat = "none";
    let backgroundSize = "none";
    let boxShadow = "none";

    if (panel.front_laminate) {
      if (panel.front_laminate.image_url) {
        backgroundSize = `${
          panel.front_laminate.image_width / renderingSize
        }px ${panel.front_laminate.image_height / renderingSize}px`;
      }
    }

    if (panel.is_removed) {
      backgroundColor = "transparent";
      backgroundImage = "none";
    }

    if (panel.orientation != "surface") {
      // Edge
      if (componentIdentifier === "removable_shelf") {
        border = "1px dashed black";
      } else {
        // Fixed partition - fill with black.
        backgroundColor = "black";
      }
    }

    style = {
      border: border,
      backgroundColor: backgroundColor,
      backgroundImage: backgroundImage,
      backgroundRepeat: backgroundRepeat,
      backgroundSize: backgroundSize,
      boxShadow: boxShadow,
    };

    return (
      <RenderNode
        object={panel}
        className="panel-node"
        style={style}
        key={panel.uniq_id}
        renderingSize={renderingSize}
      ></RenderNode>
    );
  }
  function RenderNode(props) {
    return (
      <>
        <RenderCanvasElementForAssembly
          renderingsize={props.renderingSize}
          position={props.position}
          className={`node`}
          {...props}
        >
          {props.children}
        </RenderCanvasElementForAssembly>
      </>
    );
  }
  const getRenderingSize = (item, index) => {
    let size = item.width / 170;
    const orientation = renderingDirection[index];
    let containerHeight, containerWidth;

    if (orientation === "flex-row") {
      containerHeight = 2000;
      containerWidth = 1000;
    } else {
      containerHeight = 1000;
      containerWidth = 2000;
    }

    const heightFactor = item.height / containerHeight;
    const widthFactor = item.width / containerWidth;
    const factor = Math.max(heightFactor, widthFactor);
    return 15 * factor;
  };

  return (
    <Modal isOpen={true} fullscreen>
      <ModalBody className="pt-0 ps-5">
        {isLoading ? (
          <CoreContainer>
            <LoadingSpinner />
          </CoreContainer>
        ) : (
          <div>
            {notification.type && (
              <Notification
                color={notification.type === "error" ? "danger" : "success"}
                isOpen={isNotificationVisible}
                toggle={onDismissNotification}
              >
                {notification.message}
              </Notification>
            )}
            {assemblyDrawings &&
              assemblyDrawings.map((order_item, index) => {
                if (
                  order_item.template != null &&
                  order_item.template.base_component.panels.length <= 2
                ) {
                  return null;
                }
                return (
                  <div
                    className="pt-3 pe-3 position-relative"
                    style={{ width: "283mm", height: "205mm" }}
                    key={index}
                  >
                    <div className="d-flex justify-content-between align-items-center border-bottom border-black">
                      <div>
                        <p className="fs-5 fw-bold m-0">
                          {order_item.room_name}
                          {order_item.group_name &&
                            ` / ${order_item.group_name}`}
                          {` (${order_item.project_name})`}
                        </p>
                      </div>
                      <OrderItemLabel>{order_item.label}</OrderItemLabel>
                    </div>
                    <div
                      className="d-flex my-3"
                      style={{ width: "100%", height: "85%" }}
                    >
                      <div
                        className={`position-relative d-flex ${renderingDirection[index]} gap-5`}
                        style={{ width: "65%" }}
                      >
                        <div
                          ref={(el) =>
                            (canvasRefs.current[index] = { current: el })
                          }
                          style={{
                            position: "relative",

                            width: `${
                              renderingDirection[index] === "flex-row" && "42%"
                            }`,
                          }}
                        >
                          <ModuleNode
                            key={index}
                            module={order_item}
                            cutlist={order_item.cutlist}
                            position={
                              renderingDirection[index] === "flex-column"
                                ? { x: "50%", y: "50%" }
                                : { x: 100, y: 0 }
                            }
                            renderingSize={getRenderingSize(order_item, index)}
                          ></ModuleNode>
                        </div>

                        {order_item.template != null &&
                          order_item.template.shutters.some(
                            (shutter) => shutter.is_removed !== true
                          ) && (
                            <div
                              ref={(el) =>
                                (canvasRefs.current[
                                  index + assemblyDrawings.length
                                ] = { current: el })
                              }
                              style={{
                                position: "relative",
                                width: `${
                                  renderingDirection[index] === "flex-row" &&
                                  "42%"
                                }`,
                              }}
                            >
                              <ModuleNode
                                key={index}
                                module={order_item}
                                showShutters={true}
                                cutlist={order_item.cutlist}
                                position={
                                  renderingDirection[index] === "flex-column"
                                    ? { x: "50%", y: "50%" }
                                    : { x: 100, y: 0 }
                                }
                                renderingSize={getRenderingSize(
                                  order_item,
                                  index
                                )}
                              />
                            </div>
                          )}
                      </div>
                      <div
                        className="d-flex flex-column"
                        style={{ width: "35%", height: "100%" }}
                      >
                        <div className="ps-4 border-secondary-subtle">
                          <p className="fs-5 fw-bold">{order_item.name}</p>
                          <p className="fs-3">
                            {order_item.height} H X {order_item.width} W X
                            {order_item.depth} D
                          </p>
                          {order_item.expo_sides.length > 0 && (
                            <>
                              {order_item.expo_type && (
                                <p className="fs-6">
                                  Expo: {order_item.expo_type}
                                </p>
                              )}
                              <p className="fs-6">
                                Expo Side: {order_item.expo_sides.join(", ")}
                              </p>
                            </>
                          )}
                          {order_item.images.length > 0 && (
                            <>
                              <hr />
                              <div
                                className="d-flex gap-2 flex-wrap"
                                style={{
                                  width: "100%",
                                }}
                              >
                                {order_item.images.map((imageObject, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      padding: "10px",
                                    }}
                                  >
                                    <img
                                      style={{
                                        width: "130px",
                                        height: "125px",
                                        margin: "5px",
                                      }}
                                      src={imageObject.image}
                                      alt={
                                        imageObject.label
                                          ? imageObject.label
                                          : "Image"
                                      }
                                    />
                                    {imageObject.label && (
                                      <span>{imageObject.label}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                        <div
                          style={{
                            width: "400px",
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: "0",
                            border: "1px solid #E5E5E5",
                            marginTop: "30px",
                          }}
                        >
                          {order_item.cutlist
                            .filter((cutlist) => cutlist.panels.length > 1)

                            .filter((cutlist) =>
                              cutlist.type === "drawer"
                                ? cutlist.panels.length > 2
                                : true
                            )
                            .map((cutlist, index) => (
                              <div key={index} style={{ width: "100%" }}>
                                <div className="d-flex justify-content-between align-items-center p-2 border-bottom border-#E5E5E5">
                                  <div className="d-flex align-items-center column-gap-2">
                                    <div
                                      className="d-flex align-items-center justify-content-center text-black fw-bold"
                                      style={{
                                        width: "40px",
                                        height: "40px",
                                        border: "1px solid black",
                                        borderRadius: "50%",
                                        fontSize: "20px",
                                        backgroundColor: "transparent",
                                      }}
                                    >
                                      {cutlist.label}
                                    </div>

                                    <div
                                      className="text-capitalize"
                                      style={{
                                        fontSize: "15px",
                                      }}
                                    >
                                      {cutlist.panels.length}{" "}
                                      {cutlist.panels.length === 1
                                        ? "panel"
                                        : "panels"}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>

                        <div
                          style={{
                            width: "400px",
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: "0",
                            border: "1px solid #E5E5E5",
                            marginTop: "30px",
                          }}
                        >
                          {(() => {
                            const labelCounts = {};

                            order_item.cutlist.forEach((cutlist, index) => {
                              const baseLabelMatch =
                                cutlist.label.match(/^(FS|RS|VP|HP|S)/);
                              const baseLabel = baseLabelMatch
                                ? baseLabelMatch[0]
                                : null;

                              if (baseLabel) {
                                if (!labelCounts[baseLabel]) {
                                  labelCounts[baseLabel] = 0;
                                }
                                labelCounts[baseLabel] += 1;
                              }
                            });

                            const PartitionLabelsToDisplay = Object.keys(
                              labelCounts
                            ).map((label) => (
                              <div key={label} style={{ width: "100%" }}>
                                <div className="d-flex justify-content-between align-items-center p-2 border-bottom border-#E5E5E5">
                                  <div className="d-flex align-items-center column-gap-2">
                                    <div
                                      className="d-flex align-items-center justify-content-center text-black fw-bold"
                                      style={{
                                        width: "40px",
                                        height: "40px",
                                        border: "1px solid black",
                                        borderRadius: "50%",
                                        fontSize: "20px",
                                        backgroundColor: "transparent",
                                      }}
                                    >
                                      {label}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "15px",
                                      }}
                                    >
                                      × {labelCounts[label]} no
                                      {labelCounts[label] > 1 ? "s" : ""}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ));

                            return [...PartitionLabelsToDisplay];
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default Assembly;
